<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import type { ISceneObj } from '../../types/SceneType';
	import ControlSceneEnable from '../Controls/Scene/ControlSceneEnable.svelte';
	import ControlScenePlay from '../Controls/Scene/ControlScenePlay.svelte';

	export let flows: ISceneObj[] = [];
	export let activeFlowId = '';
	export let showDraft = false;
	export let onSelect: (flowId: string) => void;
	export let onCreate: () => void;
	export let flowUsage = { flows: 0, islands: 0 };
	export let flowLimits = { flows: 0, islands: 0 };
	export let accountTier: 'free' | 'premium' = 'free';
	export let createDisabled = false;

	let query = '';
	let isCollapsed = false;
	let isMobile = false;
	let isListOpen = false;
	let searchFocused = false;

	onMount(() => {
		const mobileQuery = window.matchMedia('(max-width: 700px)');
		const syncLayout = () => {
			isMobile = mobileQuery.matches;
			if (isMobile) isCollapsed = false;
			else isListOpen = false;
		};

		syncLayout();
		mobileQuery.addEventListener('change', syncLayout);
		return () => mobileQuery.removeEventListener('change', syncLayout);
	});

	function toggleCollapsed() {
		isCollapsed = !isCollapsed;
	}

	function toggleList() {
		if (isMobile) isListOpen = !isListOpen;
	}

	function selectFlow(id: string) {
		isListOpen = false;
		onSelect(id);
	}

	function createFlow() {
		isListOpen = false;
		onCreate();
	}

	function flowId(flow: ISceneObj): string {
		return String(flow._id || flow.id);
	}

	function flowName(flow: ISceneObj): string {
		return flow._meta?.name?.trim() || $format('label_unnamed_flow', { default: 'Unnamed flow' });
	}

	function nodeCount(flow: ISceneObj): number {
		const nodes = (flow._meta as any)?.flowGraph?.nodes;
		if (Array.isArray(nodes)) return nodes.length;
		if (nodes && typeof nodes === 'object') return Object.keys(nodes).length;
		return 0;
	}

	$: normalizedQuery = query.trim().toLocaleLowerCase();
	$: visibleFlows = normalizedQuery
		? flows.filter((flow) => flowName(flow).toLocaleLowerCase().includes(normalizedQuery))
		: flows;
	$: activeFlow = flows.find((flow) => flowId(flow) === activeFlowId);
	$: activeFlowLabel =
		activeFlowId === 'new'
			? $format('label_new_flow', { default: 'New flow' })
			: activeFlow
				? flowName(activeFlow)
				: $format('label_flows', { default: 'Flows' });
</script>

<aside
	class="flow-navigator"
	class:collapsed={isCollapsed}
	aria-label={$format('label_flows', { default: 'Flows' })}
>
	<div class="navigator-header">
		<div class="navigator-title-row">
			<button
				type="button"
				class="navigator-title"
				on:click={toggleList}
				aria-label={`Select flow. Current flow: ${activeFlowLabel}`}
				aria-expanded={isMobile ? isListOpen : undefined}
			>
				<i class="fas fa-diagram-project" aria-hidden="true"></i>
				<strong>
					<span class="desktop-title">{$format('label_flows', { default: 'Flows' })}</span>
					<span class="mobile-title">{activeFlowLabel}</span>
				</strong>
				<i class="fas fa-chevron-{isListOpen ? 'up' : 'down'} mobile-chevron" aria-hidden="true"
				></i>
			</button>
			<button
				type="button"
				class="new-flow-button"
				on:click={createFlow}
				aria-label={$format('label_new_flow', { default: 'New flow' })}
				title={createDisabled
					? `Your ${accountTier} plan flow limit has been reached`
					: $format('label_new_flow', { default: 'New flow' })}
				disabled={createDisabled}
			>
				<i class="fas fa-plus" aria-hidden="true"></i>
			</button>
			<button
				type="button"
				class="collapse-button"
				on:click={toggleCollapsed}
				aria-label={isCollapsed ? 'Expand flow sidebar' : 'Collapse flow sidebar'}
				aria-expanded={!isCollapsed}
				title={isCollapsed ? 'Expand flow sidebar' : 'Collapse flow sidebar'}
			>
				<i class="fas {isCollapsed ? 'fa-angles-right' : 'fa-angles-left'}" aria-hidden="true"
				></i>
			</button>
		</div>

		<label class:expanded={searchFocused || Boolean(query)} class="flow-search">
			<i class="fas fa-search" aria-hidden="true"></i>
			<span class="sr-only">{$format('label_search', { default: 'Search flows' })}</span>
			<input
				type="search"
				bind:value={query}
				on:focus={() => {
					searchFocused = true;
					if (isMobile) isListOpen = true;
				}}
				on:blur={() => (searchFocused = false)}
				placeholder={$format('label_search', { default: 'Search flows' })}
			/>
		</label>
		<div class="usage-summary" aria-label="Flow plan usage">
			<span class="tier-badge tier-{accountTier}"
				><i class="fas {accountTier === 'premium' ? 'fa-crown' : 'fa-user'}" aria-hidden="true"
				></i>{accountTier === 'premium' ? 'Premium' : 'Free'}</span
			>
			<span title="Saved flows"
				><i class="fas fa-diagram-project" aria-hidden="true"></i>{flowUsage.flows} / {flowLimits.flows ===
				0
					? '∞'
					: flowLimits.flows}</span
			>
			<span title="Execution islands"
				><i class="fas fa-circle-nodes" aria-hidden="true"></i>{flowUsage.islands} / {flowLimits.islands ===
				0
					? '∞'
					: flowLimits.islands}</span
			>
		</div>
	</div>

	<nav
		class:mobile-open={isMobile && isListOpen}
		class="flow-list"
		aria-label={$format('label_flows', { default: 'Flows' })}
	>
		{#if activeFlowId === 'new' && showDraft}
			<button type="button" class="flow-item active" aria-current="page" on:click={createFlow}>
				<span class="flow-icon new"><i class="fas fa-plus" aria-hidden="true"></i></span>
				<span class="flow-copy">
					<span class="flow-name">{$format('label_new_flow', { default: 'New flow' })}</span>
					<span class="flow-meta">{$format('label_unsaved', { default: 'Unsaved' })}</span>
				</span>
			</button>
		{/if}

		{#each visibleFlows as flow (flowId(flow))}
			{@const id = flowId(flow)}
			{@const count = nodeCount(flow)}
			{@const flowWithId = { ...flow, id }}
			<div class:active={id === activeFlowId} class="flow-row">
				<button
					type="button"
					class="flow-select"
					aria-current={id === activeFlowId ? 'page' : undefined}
					on:click={() => selectFlow(id)}
				>
					<span
						class="flow-icon"
						style:background={flow._meta?.backgroundColor || 'rgb(102, 96, 237)'}
					>
						<i class="fas {flow._meta?.image || 'fa-diagram-project'}" aria-hidden="true"
						></i>
					</span>
					<span class="flow-copy">
						<span class="flow-name">{flowName(flow)}</span>
						<span class="flow-meta">
							{count}
							{count === 1 ? 'node' : 'nodes'}
						</span>
					</span>
				</button>
				<div class="flow-quick-actions" aria-label={`${flowName(flow)} actions`}>
					<ControlSceneEnable item={flowWithId} variant="dashControlWidget" />
					<ControlScenePlay item={flowWithId} variant="dashControlWidget" />
				</div>
			</div>
		{/each}

		{#if visibleFlows.length === 0 && !(activeFlowId === 'new' && showDraft)}
			<p class="no-results">
				{query
					? $format('label_no_results', { default: 'No matching flows' })
					: $format('label_no_flows_yet', { default: 'No flows yet' })}
			</p>
		{/if}
	</nav>

	<p class="navigator-hint">
		<i class="fas fa-copy" aria-hidden="true"></i>
		Copy nodes, switch flows, then paste.
	</p>
</aside>

<style>
	.flow-navigator {
		width: 280px;
		min-width: 280px;
		height: 100%;
		min-height: 0;
		display: flex;
		flex-direction: column;
		background: var(--color-1, #202225);
		border-right: 1px solid var(--color-3, rgba(148, 163, 184, 0.22));
		color: inherit;
		z-index: 110;
	}

	.navigator-header {
		padding: 14px 12px 10px;
		border-bottom: 1px solid var(--color-3, rgba(148, 163, 184, 0.16));
	}

	.navigator-title-row {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 32px 32px;
		align-items: center;
		gap: 8px;
		margin-bottom: 12px;
	}

	.navigator-title {
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 0;
		padding: 0;
		border: 0;
		background: transparent;
		color: inherit;
		text-align: left;
	}

	.navigator-title i {
		color: var(--color-primary, #6366f1);
	}

	.navigator-title strong {
		font-size: 15px;
		line-height: 20px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.mobile-title,
	.mobile-chevron {
		display: none;
	}

	.new-flow-button,
	.collapse-button {
		width: 32px;
		height: 32px;
		border: 0;
		border-radius: 8px;
		background: transparent;
		color: inherit;
		cursor: pointer;
	}

	.new-flow-button:hover,
	.collapse-button:hover {
		background: var(--color-2, rgba(148, 163, 184, 0.14));
	}

	.new-flow-button {
		color: var(--color-primary, #6366f1);
	}
	.new-flow-button:disabled {
		opacity: 0.38;
		cursor: not-allowed;
	}
	.usage-summary {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 9px;
		font-size: 10px;
		color: #94a3b8;
	}
	.usage-summary > span {
		display: inline-flex;
		align-items: center;
		gap: 4px;
	}
	.tier-badge {
		border-radius: 999px;
		padding: 2px 6px;
		background: rgba(148, 163, 184, 0.12);
	}
	.tier-premium {
		color: #fde68a;
		background: rgba(250, 204, 21, 0.12);
	}

	.collapse-button {
		color: #9ca3af;
	}

	.flow-search {
		display: flex;
		align-items: center;
		gap: 8px;
		height: 36px;
		padding: 0 10px;
		border: 1px solid var(--color-3, rgba(148, 163, 184, 0.22));
		border-radius: 8px;
		background: var(--color-base, #151617);
		color: #9ca3af;
	}

	.flow-search input {
		width: 100%;
		min-width: 0;
		border: 0;
		outline: 0;
		background: transparent;
		color: inherit;
		font: inherit;
	}

	.flow-list {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		padding: 8px;
	}

	.flow-row,
	.flow-item {
		width: 100%;
		display: flex;
		align-items: center;
		border: 1px solid transparent;
		border-radius: 9px;
		background: transparent;
		color: inherit;
	}

	.flow-item {
		gap: 10px;
		padding: 9px 10px;
		text-align: left;
		cursor: pointer;
	}

	.flow-row {
		padding: 3px 4px;
	}

	.flow-select {
		min-width: 0;
		flex: 1;
		display: flex;
		align-items: center;
		gap: 9px;
		padding: 6px;
		border: 0;
		background: transparent;
		color: inherit;
		text-align: left;
		cursor: pointer;
	}

	.flow-row:hover,
	.flow-item:hover {
		background: var(--color-2, rgba(148, 163, 184, 0.1));
	}

	.flow-row.active,
	.flow-item.active {
		border-color: color-mix(in srgb, var(--color-primary, #6366f1) 42%, transparent);
		background: color-mix(in srgb, var(--color-primary, #6366f1) 15%, transparent);
	}

	.flow-quick-actions {
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		gap: 2px;
	}

	.flow-quick-actions :global(button) {
		width: 28px;
		height: 28px;
		min-width: 28px;
		min-height: 28px;
	}

	.flow-icon {
		width: 34px;
		height: 34px;
		flex: 0 0 34px;
		display: grid;
		place-items: center;
		border-radius: 9px;
		color: white;
		box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
	}

	.flow-icon.new {
		background: var(--color-primary, #6366f1);
	}

	.flow-copy {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 3px;
	}

	.flow-name,
	.flow-meta {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.flow-name {
		font-size: 13px;
		font-weight: 600;
	}

	.flow-meta {
		display: flex;
		align-items: center;
		gap: 4px;
		font-size: 11px;
		color: #9ca3af;
	}

	.flow-navigator.collapsed {
		width: 64px;
		min-width: 64px;
	}

	.flow-navigator.collapsed .navigator-header {
		padding: 10px 8px;
	}

	.flow-navigator.collapsed .navigator-title-row {
		display: flex;
		justify-content: center;
		margin: 0;
	}

	.flow-navigator.collapsed .navigator-title,
	.flow-navigator.collapsed .new-flow-button,
	.flow-navigator.collapsed .flow-search,
	.flow-navigator.collapsed .usage-summary,
	.flow-navigator.collapsed .flow-copy,
	.flow-navigator.collapsed .flow-quick-actions,
	.flow-navigator.collapsed .navigator-hint {
		display: none;
	}

	.flow-navigator.collapsed .flow-list {
		padding: 8px 6px;
	}

	.flow-navigator.collapsed .flow-row,
	.flow-navigator.collapsed .flow-item {
		padding: 3px;
	}

	.flow-navigator.collapsed .flow-select,
	.flow-navigator.collapsed .flow-item {
		justify-content: center;
		padding: 5px;
	}

	.no-results {
		padding: 24px 12px;
		margin: 0;
		color: #9ca3af;
		font-size: 13px;
		text-align: center;
	}

	.navigator-hint {
		margin: 0;
		padding: 10px 12px;
		border-top: 1px solid var(--color-3, rgba(148, 163, 184, 0.16));
		color: #9ca3af;
		font-size: 11px;
		line-height: 16px;
	}

	.navigator-hint i {
		margin-right: 5px;
	}

	.sr-only {
		position: absolute;
		width: 1px;
		height: 1px;
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		white-space: nowrap;
		border: 0;
	}

	@media (max-width: 1000px) {
		.flow-navigator {
			width: 240px;
			min-width: 240px;
		}
	}

	@media (max-width: 700px) {
		.flow-navigator {
			position: relative;
			width: 100%;
			min-width: 0;
			height: 52px;
			min-height: 52px;
			overflow: visible;
			border-right: 0;
			border-bottom: 1px solid var(--color-3, rgba(148, 163, 184, 0.22));
			z-index: 180;
		}

		.navigator-header {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 8px;
		}

		.usage-summary,
		.collapse-button,
		.navigator-hint {
			display: none;
		}

		.navigator-title-row {
			flex: 1;
			min-width: 0;
			margin: 0;
			grid-template-columns: minmax(0, 1fr) 36px;
		}

		.navigator-title {
			min-width: 0;
			cursor: pointer;
		}

		.navigator-title strong {
			min-width: 0;
			flex: 1;
		}

		.desktop-title {
			display: none;
		}

		.mobile-title,
		.mobile-chevron {
			display: inline;
		}

		.mobile-chevron {
			flex: 0 0 auto;
			font-size: 11px;
			color: #9ca3af !important;
		}

		.new-flow-button {
			width: 36px;
			height: 36px;
		}

		.flow-search {
			width: 36px;
			flex: 0 0 auto;
			padding: 0 10px;
			transition: width 160ms ease;
		}

		.flow-search.expanded {
			width: 33.333vw;
		}

		.flow-search input {
			opacity: 0;
			pointer-events: none;
			transition: opacity 120ms ease;
		}

		.flow-search.expanded input {
			opacity: 1;
			pointer-events: auto;
		}

		.flow-list {
			position: absolute;
			top: 100%;
			left: 0;
			right: 0;
			display: none;
			max-height: min(52vh, 420px);
			overflow-x: hidden;
			overflow-y: auto;
			padding: 8px;
			border-bottom: 1px solid var(--color-3, rgba(148, 163, 184, 0.22));
			background: var(--color-1, #202225);
			box-shadow: 0 16px 28px rgba(0, 0, 0, 0.32);
			overscroll-behavior: contain;
		}

		.flow-list.mobile-open {
			display: block;
		}

		.flow-row,
		.flow-item {
			width: 100%;
			min-width: 0;
		}

		.flow-item {
			padding: 6px 8px;
		}

		.flow-icon {
			width: 28px;
			height: 28px;
			flex-basis: 28px;
		}
	}
</style>
